import { useEffect, useState } from 'react';
import { MASKED } from 'shared';
import { api, ApiError } from '../lib/api';

/**
 * Secret fields load as "********" from the server — the real value is never sent.
 * Saving without touching such a field sends the same mask back, and the server treats
 * that as "keep the stored value".
 */
interface Section {
  key: string;
  title: string;
  /** Short form for the tab strip — the full title still heads the panel. */
  tab: string;
  hint: string;
  path: string;
  fields: { name: string; label: string; secret?: boolean; type?: string }[];
}

const SECTIONS: Section[] = [
  {
    key: 'smtp', title: 'SMTP', tab: 'SMTP', path: '/config/smtp',
    hint: 'Dipakai untuk reset password dan notifikasi.',
    fields: [
      { name: 'host', label: 'Host' },
      { name: 'port', label: 'Port', type: 'number' },
      { name: 'username', label: 'Username' },
      { name: 'password', label: 'Password', secret: true },
      { name: 'fromEmail', label: 'Email pengirim' },
      { name: 'fromName', label: 'Nama pengirim' },
    ],
  },
  {
    key: 'aiyo-gateway', title: 'AiYO Bills Invoice', tab: 'AiYO Bills', path: '/config/aiyo-gateway',
    hint: 'Payment gateway. Dipakai mulai Fase 5.',
    fields: [
      { name: 'baseUrl', label: 'Base URL' },
      { name: 'apiKey', label: 'API Key' },
      { name: 'apiSecret', label: 'API Secret', secret: true },
      { name: 'username', label: 'Username' },
      { name: 'password', label: 'Password', secret: true },
      { name: 'billMasterId', label: 'Bill Master ID' },
      { name: 'callbackUrl', label: 'Callback URL' },
    ],
  },
  {
    key: 'microsite', title: 'AiYO Microsite SSO', tab: 'Microsite SSO', path: '/config/microsite',
    hint: 'Identitas pendonor. Dipakai mulai Fase 3.',
    fields: [
      { name: 'baseUrl', label: 'Base URL' },
      { name: 'apiKey', label: 'API Key', secret: true },
    ],
  },
  {
    key: 'storage', title: 'Penyimpanan S3', tab: 'Penyimpanan S3', path: '/config/storage',
    hint: 'Gambar kegiatan dan surat permohonan rumah sakit. Sistem menyimpan URL publik, '
      + 'jadi bucket harus bisa dibaca tanpa kredensial — pakai Uji koneksi untuk memastikannya.',
    fields: [
      { name: 'endpoint', label: 'Endpoint' },
      { name: 'region', label: 'Region' },
      { name: 'bucket', label: 'Bucket' },
      { name: 'accessKeyId', label: 'Access Key ID' },
      { name: 'secretAccessKey', label: 'Secret Access Key', secret: true },
      { name: 'publicBaseUrl', label: 'Public base URL' },
    ],
  },
];

type Values = Record<string, unknown>;

function SectionForm({ section }: { section: Section }) {
  const [values, setValues] = useState<Values>({});
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<Values>(section.path)
      .then(setValues)
      .catch((err) => setMsg({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'Gagal memuat konfigurasi.',
      }));
  }, [section.path]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const payload: Values = {};
      for (const f of section.fields) {
        const v = values[f.name];
        // The mask goes back untouched; the server reads it as "do not change".
        payload[f.name] = v === '' ? null : v;
      }
      setValues(await api.put<Values>(section.path, payload));
      setMsg({ kind: 'ok', text: 'Konfigurasi disimpan.' });
    } catch (err) {
      setMsg({
        kind: 'error',
        text: err instanceof ApiError ? err.message : 'Gagal menyimpan konfigurasi.',
      });
    } finally {
      setBusy(false);
    }
  }

  const configured = (values.configured ?? {}) as Record<string, boolean>;

  return (
    <form onSubmit={save} className="card stack">
      <div>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3>{section.title}</h3>
          {values.source ? <span className="pill">{String(values.source)}</span> : null}
        </div>
        <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: 13.5 }}>{section.hint}</p>
      </div>

      {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

      <div className="grid-2">
        {section.fields.map((f) => (
          <div className="field" key={f.name}>
            <label className="label" htmlFor={`${section.key}-${f.name}`}>
              {f.label}
              {f.secret && configured[f.name] ? (
                <span className="pill accent" style={{ marginLeft: 6 }}>terisi</span>
              ) : null}
            </label>
            <input
              id={`${section.key}-${f.name}`}
              type={f.type ?? 'text'}
              value={(values[f.name] ?? '') as string | number}
              placeholder={f.secret ? 'kosongkan untuk mempertahankan nilai lama' : ''}
              onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
            />
          </div>
        ))}
      </div>

      <div className="row">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Menyimpan…' : 'Simpan'}
        </button>
        {(section.key === 'smtp' || section.key === 'storage') && (
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const r = await api.post<{ ok: boolean; message: string }>(
                  `/config/${section.key}/test`, {},
                );
                setMsg({ kind: r.ok ? 'ok' : 'error', text: r.message });
              } catch (err) {
                setMsg({
                  kind: 'error',
                  text: err instanceof ApiError ? err.message : 'Uji koneksi gagal.',
                });
              } finally {
                setBusy(false);
              }
            }}
          >
            Uji koneksi
          </button>
        )}
      </div>
    </form>
  );
}

function PlatformForm() {
  const [approval, setApproval] = useState<boolean | null>(null);

  useEffect(() => {
    api.get<{ donorRegistrationRequiresApproval: boolean }>('/config/platform')
      .then((d) => setApproval(d.donorRegistrationRequiresApproval))
      .catch(() => setApproval(null));
  }, []);

  return (
    <div className="card stack">
      <div>
        <h3>Pendaftaran pendonor</h3>
        <p className="muted" style={{ margin: '0.25rem 0 0', fontSize: 13.5 }}>
          Berpengaruh mulai Fase 3, saat microsite pendonor aktif.
        </p>
      </div>
      <label className="row" style={{ gap: '0.6rem', cursor: 'pointer' }}>
        <input
          type="checkbox"
          style={{ width: 'auto' }}
          checked={approval ?? false}
          disabled={approval === null}
          onChange={async (e) => {
            const next = e.target.checked;
            setApproval(next);
            await api.put('/config/platform', { donorRegistrationRequiresApproval: next });
          }}
        />
        <span style={{ fontSize: 14 }}>Pendaftaran pendonor baru perlu approval PMI</span>
      </label>
    </div>
  );
}

const TABS = [
  { key: 'platform', label: 'Platform' },
  ...SECTIONS.map((s) => ({ key: s.key, label: s.tab })),
];

export function Settings() {
  const [active, setActive] = useState(TABS[0]!.key);

  /**
   * Panels stay mounted and are hidden rather than unmounted: switching tabs must not
   * silently throw away half-typed credentials, and it keeps the one fetch per section
   * that the page has always done.
   */
  const panel = (key: string, node: React.ReactNode) => (
    <div
      key={key}
      role="tabpanel"
      id={`panel-${key}`}
      aria-labelledby={`tab-${key}`}
      hidden={active !== key}
    >
      {node}
    </div>
  );

  function onTabKeyDown(e: React.KeyboardEvent, index: number) {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = TABS[(index + step + TABS.length) % TABS.length]!;
    setActive(next.key);
    document.getElementById(`tab-${next.key}`)?.focus();
  }

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Kredensial &amp; Pengaturan</h2>
        <p>
          Nilai rahasia tampil sebagai <span className="mono">{MASKED}</span> dan tidak pernah
          dikirim keluar dari server. Kosongkan field untuk mempertahankan nilai tersimpan.
        </p>
      </div>

      <div className="tabs" role="tablist" aria-label="Kelompok pengaturan">
        {TABS.map((t, i) => (
          <button
            key={t.key}
            id={`tab-${t.key}`}
            className="tab"
            type="button"
            role="tab"
            aria-selected={active === t.key}
            aria-controls={`panel-${t.key}`}
            tabIndex={active === t.key ? 0 : -1}
            onClick={() => setActive(t.key)}
            onKeyDown={(e) => onTabKeyDown(e, i)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {panel('platform', <PlatformForm />)}
      {SECTIONS.map((s) => panel(s.key, <SectionForm section={s} />))}
    </div>
  );
}
